import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/streamApi';
import { SOS_CHANGED, forgetSos, rememberedSos, stopActiveSos } from '../utils/sosState';

// Shown on every page while an SOS is active, so it can always be stopped —
// closing the "Panic Sent!" window no longer hides the only Stop button
// (PRVSK-122). Polls the box; a stop from another device clears it here too.
const POLL_MS = 10000;

const fmtTime = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default function SosActiveBanner() {
  const [sos, setSos]       = useState(null);   // /admin/panic/active body when active
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState('');
  const [stopped, setStopped] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/admin/panic/active');
      if (!r.ok) return;
      const d = await r.json();
      if (d.active) { setSos(d); return; }
      setSos(null);
      if (rememberedSos()) forgetSos();   // stopped elsewhere
    } catch { /* offline — keep what we last knew */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener(SOS_CHANGED, onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(SOS_CHANGED, onFocus);
    };
  }, [load]);

  const stop = async () => {
    if (busy) return;
    setBusy(true); setMsg('');
    try {
      const res = await stopActiveSos();
      if (res.ok) {
        setStopped(true);
        setTimeout(() => { setStopped(false); setSos(null); }, 2500);
      } else {
        setMsg(res.message);
        load();
      }
    } catch {
      setMsg('Could not stop the SOS. Check the connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!sos && !stopped) return null;

  const canStop = stopped || sos?.can_stop || !!rememberedSos();
  const who = sos?.triggered_by ? ` by ${sos.triggered_by}` : '';

  return (
    <div role="alert" style={{
      position: 'fixed', left: 8, right: 8, zIndex: 9999,
      bottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
      maxWidth: 560, margin: '0 auto', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '10px 14px', background: stopped ? '#15803d' : '#b91c1c', color: '#fff', fontSize: 14,
    }}>
      {stopped ? (
        <span style={{ flex: '1 1 auto', fontWeight: 600 }}>✅ SOS stopped</span>
      ) : (
        <>
          <span style={{ flex: '1 1 200px', minWidth: 0 }}>
            <b>🆘 SOS active</b> since {fmtTime(sos.started)}{who}.
            {!canStop && <span style={{ display: 'block', fontSize: 12, opacity: 0.9 }}>
              The person who raised it or an admin can stop it.</span>}
            {msg && <span style={{ display: 'block', fontSize: 12, marginTop: 2 }}>{msg}</span>}
          </span>
          {canStop && (
            <button type="button" onClick={stop} disabled={busy} style={{
              background: '#fff', color: '#b91c1c', border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, fontWeight: 700,
              cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
            }}>{busy ? 'Stopping…' : 'Stop SOS'}</button>
          )}
        </>
      )}
    </div>
  );
}
